import React, { useState, useEffect, Suspense, lazy } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ClienteAuthProvider, useClienteAuth } from './context/ClienteAuthContext';
import { ProveedorAuthProvider, useProveedorAuth } from './context/ProveedorAuthContext';
import { UiProvider, useUi } from './context/UiContext';
import Login from './components/Login';
import ErrorBoundary from './components/ErrorBoundary';
import MiCuentaMenu from './components/shared/MiCuentaMenu';
import InstallPrompt from './components/shared/InstallPrompt';
import './App.css';

// Code splitting: cada panel de rol se carga bajo demanda. El bundle inicial
// sólo trae Login + ErrorBoundary + el shell. El resto cae como chunks
// separados cuando el rol entra a su panel.
const AdminPanel         = lazy(() => import('./components/admin/AdminLayout'));
const SupervisorPanel    = lazy(() => import('./components/supervisor/SupervisorPanel'));
const MaestroPanel       = lazy(() => import('./components/maestro/MaestroPanel'));
const GerenciaPanel      = lazy(() => import('./components/gerencia/GerenciaPanel'));
// Sprint 16.1 — Administrador de Condominio (Ley 21.442)
const CondominioPanel    = lazy(() => import('./components/condominio/CondominioPanel'));
const SuperadminPanel    = lazy(() => import('./components/superadmin/SuperadminPanel'));
const ClienteLogin         = lazy(() => import('./components/cliente/ClienteLogin'));
const ClienteAuthCallback  = lazy(() => import('./components/cliente/ClienteAuthCallback'));
const ClientePanel         = lazy(() => import('./components/cliente/ClientePanel'));
const ClienteCaso          = lazy(() => import('./components/cliente/ClienteCaso'));
const ClienteMantenciones  = lazy(() => import('./components/cliente/ClienteMantenciones'));
const ClienteHistorial     = lazy(() => import('./components/cliente/ClienteHistorial'));
const ClienteDocumentos    = lazy(() => import('./components/cliente/ClienteDocumentos'));
const ClienteVera          = lazy(() => import('./components/cliente/ClienteVera'));
const ClienteValidar       = lazy(() => import('./components/cliente/ClienteValidar'));
const ProveedorPanel       = lazy(() => import('./components/proveedor/ProveedorPanel'));
const ProveedorLogin       = lazy(() => import('./components/proveedor/ProveedorLogin'));
const ProveedorPostular    = lazy(() => import('./components/proveedor/ProveedorPostular'));

function LoadingView({ label = 'Cargando…' }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 48, fontSize: 13, color: '#888780',
    }}>{label}</div>
  );
}

function ThemeToggle() {
  const { theme, toggleTheme } = useUi();
  const isDark = theme === 'dark';
  return (
    <button
      type="button"
      className="btn-theme-toggle"
      onClick={toggleTheme}
      aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
      title={isDark ? 'Modo claro' : 'Modo oscuro'}
    >
      {isDark ? (
        // Sun icon
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      ) : (
        // Moon icon
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      )}
    </button>
  );
}

function AppContent() {
  const { user, ejecutivo, loading, logout, isAdmin, isSupervisor, isMaestro, isGerencia, isSuperadmin, isAdminCondominio } = useAuth();

  // Si el usuario logueado no tiene rol específico y aterriza en un path
  // de rol ajeno (legacy /app, o /admin/.../etc sin permisos), lo mandamos
  // a /. Cada rol canoniza su propio path en el render; este effect se
  // encarga del único caso donde nadie más reclama la URL.
  useEffect(() => {
    if (typeof window === 'undefined' || loading || !user) return;
    if (isAdmin || isSupervisor || isMaestro || isGerencia || isSuperadmin || isAdminCondominio) return;
    const path = window.location.pathname;
    const enRolAjeno = path.startsWith('/admin')
                    || path.startsWith('/supervisor')
                    || path.startsWith('/maestro')
                    || path.startsWith('/gerencia')
                    || path.startsWith('/superadmin')
                    || path.startsWith('/condominio')
                    || path.startsWith('/app');
    if (enRolAjeno) window.history.replaceState({}, '', '/');
  }, [loading, user, isAdmin, isSupervisor, isMaestro, isGerencia, isSuperadmin, isAdminCondominio]);

  if (loading) return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#f5f4f0', fontSize: '13px', color: '#888780',
    }}>
      Cargando...
    </div>
  );

  if (!user) return <Login />;

  // Panel de administración — vista exclusiva por rol.
  // Canonicaliza la URL a /admin (mismo patrón que supervisor/maestro/gerencia).
  if (isAdmin) {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin')) {
      window.history.replaceState({}, '', '/admin');
    }
    return (
      <>
        <Suspense fallback={<LoadingView label="Cargando panel admin…" />}>
          <AdminPanel
            ejecutivo={ejecutivo}
            logout={logout}
          />
        </Suspense>
        <InstallPrompt />
      </>
    );
  }

  // Panel de supervisor — vista exclusiva por rol.
  // Canonicaliza la URL a /supervisor para que el supervisor siempre tenga
  // su propio path (no el legacy /app que comparte con admin).
  if (isSupervisor) {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/supervisor')) {
      window.history.replaceState({}, '', '/supervisor');
    }
    return (
      <>
        <Suspense fallback={<LoadingView label="Cargando panel supervisor…" />}>
          <SupervisorPanel logout={logout} />
        </Suspense>
        <InstallPrompt />
      </>
    );
  }

  // Panel de superadmin (dueños de Vera) — vista exclusiva.
  // Canonicaliza a /superadmin. El superadmin no pertenece a una inmobiliaria
  // específica: ve todas las cuentas clientes del SaaS.
  if (isSuperadmin) {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/superadmin')) {
      window.history.replaceState({}, '', '/superadmin');
    }
    return (
      <>
        <Suspense fallback={<LoadingView label="Cargando panel superadmin…" />}>
          <SuperadminPanel logout={logout} />
        </Suspense>
        <InstallPrompt />
      </>
    );
  }

  // Panel de gerencia — vista exclusiva por rol.
  // Canonicaliza a /gerencia (mismo patrón que supervisor/maestro).
  if (isGerencia) {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/gerencia')) {
      window.history.replaceState({}, '', '/gerencia');
    }
    return (
      <>
        <Suspense fallback={<LoadingView label="Cargando panel gerencia…" />}>
          <GerenciaPanel logout={logout} />
        </Suspense>
        <InstallPrompt />
      </>
    );
  }

  // Panel del Administrador de Condominio (Sprint 16.1 — Ley 21.442).
  // Canonicaliza a /condominio. Vive en su propio path: este rol es
  // contraparte legal del admin de la inmobiliaria, no parte de su equipo.
  if (isAdminCondominio) {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/condominio')) {
      window.history.replaceState({}, '', '/condominio');
    }
    return (
      <>
        <Suspense fallback={<LoadingView label="Cargando panel del administrador…" />}>
          <CondominioPanel logout={logout} />
        </Suspense>
        <InstallPrompt />
      </>
    );
  }

  // Portal del maestro/trabajador — mobile-first.
  // Canonicaliza la URL a /maestro para que el portal tenga su propio path
  // (mismo patrón que el supervisor en /supervisor — no comparte el legacy /app).
  if (isMaestro) {
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/maestro')) {
      window.history.replaceState({}, '', '/maestro');
    }
    return (
      <>
        <Suspense fallback={<LoadingView label="Cargando portal del maestro…" />}>
          <MaestroPanel logout={logout} />
        </Suspense>
        <InstallPrompt />
      </>
    );
  }

  // Fallback: usuario logueado sin un rol específico (admin/supervisor/
  // maestro/gerencia/superadmin). Hoy nadie está en este caso (cero
  // usuarios con rol='ejecutivo' en producción). Si llega alguien aquí,
  // mostramos un mensaje simple en vez del shell legacy completo que
  // existía antes del refactor de roles.
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', padding: '24px', textAlign: 'center', gap: 16,
      background: 'var(--bg-app)', color: 'var(--text-strong)', fontFamily: 'inherit',
    }}>
      <img src="/vera.png" alt="" width={96} height={96} style={{ borderRadius: 16 }} />
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Tu cuenta no tiene un panel asignado</h1>
      <p style={{ margin: 0, fontSize: 14, color: 'var(--text-soft)', maxWidth: 420, lineHeight: 1.5 }}>
        Estás logueado pero todavía no tienes un rol específico configurado
        (admin, supervisor, maestro o gerencia). Pedí a tu contacto en Vera
        que actualice tu cuenta.
      </p>
      <button
        type="button"
        onClick={logout}
        style={{
          padding: '10px 18px', background: 'var(--vera-orange)', color: '#fff',
          border: 0, borderRadius: 8, fontFamily: 'inherit', fontSize: 13,
          fontWeight: 700, cursor: 'pointer',
        }}
      >
        Cerrar sesión
      </button>
      <InstallPrompt />
    </div>
  );
}

// ── Subárbol del cliente (propietario) ──────────────────────────────────────
// Vive bajo /cliente/* y NO usa Supabase Auth — usa ClienteAuthContext con
// JWT custom. Auth de ejecutivos y de cliente son universos separados.

function ClienteContent() {
  const { isAuthenticated } = useClienteAuth();
  const [path, setPath] = useState(window.location.pathname);

  // Escuchamos popstate (cuando ClienteAuthCallback hace replaceState +
  // dispatchEvent) para rerender al pathname nuevo
  useEffect(() => {
    const onChange = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onChange);
    return () => window.removeEventListener('popstate', onChange);
  }, []);

  // Determina qué pantalla renderizar según la ruta
  // Validación QR puede llegar sin sesión (lo redirige a login internamente).
  const matchValidar = path.match(/^\/cliente\/validar\/([0-9a-f-]{36})$/i);
  let body;
  if (path === '/cliente/auth') {
    // Callback del magic link (usable solo una vez)
    body = (
      <Suspense fallback={<LoadingView label="Verificando enlace…" />}>
        <ClienteAuthCallback />
      </Suspense>
    );
  } else if (matchValidar) {
    body = (
      <Suspense fallback={<LoadingView label="Verificando código…" />}>
        <ClienteValidar token={matchValidar[1]} />
      </Suspense>
    );
  } else if (!isAuthenticated || path === '/cliente/login') {
    // Form de RUT cuando no hay sesión
    body = (
      <Suspense fallback={<LoadingView label="Cargando…" />}>
        <ClienteLogin />
      </Suspense>
    );
  } else {
    const matchCaso = path.match(/^\/cliente\/caso\/([0-9a-f-]{36})$/i);
    if (matchCaso) {
      // Vista detalle del caso
      body = (
        <Suspense fallback={<LoadingView label="Cargando caso…" />}>
          <ClienteCaso id={matchCaso[1]} />
        </Suspense>
      );
    } else if (path === '/cliente/mantenciones') {
      // Manual de mantenciones del propietario (Sprint 8)
      body = (
        <Suspense fallback={<LoadingView label="Cargando tus mantenciones…" />}>
          <ClienteMantenciones />
        </Suspense>
      );
    } else if (path === '/cliente/historial') {
      // Historial de casos cerrados (Sprint 10)
      body = (
        <Suspense fallback={<LoadingView label="Cargando tu historial…" />}>
          <ClienteHistorial />
        </Suspense>
      );
    } else if (path === '/cliente/documentos') {
      // Documentos (planos / EETT / garantías) de la vivienda (Sprint 13.2)
      body = (
        <Suspense fallback={<LoadingView label="Cargando tus documentos…" />}>
          <ClienteDocumentos />
        </Suspense>
      );
    } else if (path === '/cliente/vera') {
      // Chat con Vera para reportar un caso nuevo (Sprint 11)
      body = (
        <Suspense fallback={<LoadingView label="Cargando Vera…" />}>
          <ClienteVera />
        </Suspense>
      );
    } else {
      // Default: panel principal
      body = (
        <Suspense fallback={<LoadingView label="Cargando tu portal…" />}>
          <ClientePanel />
        </Suspense>
      );
    }
  }

  // InstallPrompt visible solo cuando hay sesión activa (no estorba el
  // login ni el callback). Igual al patrón del flujo ejecutivo.
  return (
    <>
      {body}
      {isAuthenticated && <InstallPrompt />}
    </>
  );
}

function isClientePath() {
  return window.location.pathname.startsWith('/cliente');
}

// ── Subárbol del proveedor (marketplace, Sprint 14) ────────────────────
// Vive bajo /proveedor/* y usa Supabase Auth nativo (no JWT custom como
// el cliente). El magic link lo manda Supabase desde su SMTP propio.
function ProveedorContent({ logout }) {
  const { isAuthenticated, tieneProveedor, loading } = useProveedorAuth();
  const path = typeof window !== 'undefined' ? window.location.pathname : '';

  // /proveedor/postular es público (no requiere auth). Se cuelga aquí
  // adentro del Provider por simplicidad — el form no usa el context.
  if (path === '/proveedor/postular') {
    return (
      <Suspense fallback={<LoadingView label="Cargando formulario…" />}>
        <ProveedorPostular />
      </Suspense>
    );
  }

  if (loading) return <LoadingView label="Cargando portal proveedor…" />;

  // Sesión activa pero sin proveedor linkeado → mensaje claro al user.
  // Ocurre si alguien con cuenta de Supabase Auth (ejecutivo, cliente
  // self-signup, etc.) entra a /proveedor sin estar en el catálogo.
  if (isAuthenticated && !tieneProveedor) {
    return (
      <div style={{
        maxWidth: 480, margin: '80px auto', padding: '32px 28px',
        background: 'var(--bg-card)', border: '0.5px solid var(--border-soft)',
        borderRadius: 12, textAlign: 'center', color: 'var(--text-soft)',
      }}>
        <h2 style={{ marginTop: 0, color: 'var(--text-strong)' }}>Sin perfil de proveedor</h2>
        <p>
          Tu email no está registrado como proveedor en Vera.
          {' '}<a href="/proveedor/postular" style={{ color: 'var(--vera-orange)', fontWeight: 600 }}>Postúlate al marketplace</a>{' '}
          o escríbenos a{' '}
          <a href="mailto:contacto@ia-vera.com" style={{ color: 'var(--vera-orange)' }}>contacto@ia-vera.com</a>.
        </p>
        <button type="button" onClick={logout} style={{
          padding: '10px 18px', background: 'var(--vera-orange)', color: '#fff',
          border: 0, borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
          fontSize: 13, fontWeight: 600, marginTop: 12,
        }}>Cerrar sesión</button>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<LoadingView />}>
        <ProveedorLogin />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoadingView />}>
      <ProveedorPanel logout={logout} />
    </Suspense>
  );
}

function isProveedorPath() {
  return window.location.pathname.startsWith('/proveedor');
}

// Wrapper que toma logout del context proveedor y se lo pasa al panel.
// Necesario porque el context se usa adentro del Provider, no afuera.
function ProveedorContentWrapper() {
  const { logout } = useProveedorAuth();
  return <ProveedorContent logout={logout} />;
}

export default function App() {
  // En boot detectamos qué subárbol montar. No hace falta watchear cambios
  // porque navegar entre /cliente, /proveedor y /app implica page load completo.
  const cliente = isClientePath();
  const proveedor = isProveedorPath();
  const isResetPwd = typeof window !== 'undefined'
    && window.location.pathname.startsWith('/reset-password');

  if (cliente) {
    return (
      <UiProvider>
        <ClienteAuthProvider>
          <ErrorBoundary>
            <ClienteContent />
          </ErrorBoundary>
        </ClienteAuthProvider>
      </UiProvider>
    );
  }

  // Subárbol del proveedor: Supabase Auth nativo. AppProveedorBoot maneja
  // logout localmente (no usa AuthContext de ejecutivos).
  if (proveedor) {
    return (
      <UiProvider>
        <ProveedorAuthProvider>
          <ErrorBoundary>
            <ProveedorContentWrapper />
          </ErrorBoundary>
        </ProveedorAuthProvider>
      </UiProvider>
    );
  }

  // Reset password: vive fuera del flujo normal de auth porque la
  // sesión transitoria del token de recovery no debe pasar por el
  // shell de admin/supervisor/etc.
  if (isResetPwd) {
    const ResetPasswordPage = lazy(() => import('./components/ResetPasswordPage'));
    return (
      <UiProvider>
        <ErrorBoundary>
          <Suspense fallback={<LoadingView label="Cargando…" />}>
            <ResetPasswordPage />
          </Suspense>
        </ErrorBoundary>
      </UiProvider>
    );
  }

  return (
    <AuthProvider>
      <UiProvider>
        <AppContent />
      </UiProvider>
    </AuthProvider>
  );
}
